import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { TooltipProps } from './Tooltip.types';

import { S } from './Tooltip.styles';

export const Tooltip = ({
  content,
  children,
  id,
  tabIndex = 0,
  'aria-label': ariaLabel
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const tooltipId = id || `tooltip-${Math.random().toString(36)}`;

  useLayoutEffect(() => {
    if (isVisible && wrapperRef.current && tooltipRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      setTooltipPosition({
        left: wrapperRect.right - 22,
        top: wrapperRect.top - tooltipRect.height - 4
      });
    }
  }, [isVisible]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ': // Support for spacebar opening/closing
        event.preventDefault();
        setIsVisible(!isVisible);
        break;
      case 'Escape':
        if (isVisible) {
          setIsVisible(false);
        }
        break;
    }
  };

  return (
    <>
      <S.Wrapper
        ref={wrapperRef}
        role="button"
        tabIndex={tabIndex}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
        onKeyDown={handleKeyDown}
        aria-describedby={tooltipId}
        aria-expanded={isVisible}
        aria-label={ariaLabel}
      >
        {children}
      </S.Wrapper>
      {isVisible &&
        createPortal(
          <S.TooltipContent
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            aria-live="polite"
            $isVisible={isVisible}
            $position={tooltipPosition}
          >
            {content}
          </S.TooltipContent>,
          document.body
        )}
    </>
  );
};
